import React, { useContext, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DashboardContext } from '../../../context/DashboardContext'
import { AuthContext } from '../../../context/AuthContext'
import useOutsideClick from '../../../hooks/useOutsideClick'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import { useSidebarStore } from '@/stores/sidebarStore'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { CircleChevronDown } from '../../icons/CircleChevronDown'
import { WorkspaceMenuPortal } from './WorkspaceMenuPortal'

export function WorkspaceSelection() {
  const { user } = useContext(AuthContext)
  const { workspaces } = useContext(DashboardContext)
  const [openMenu, setOpenMenu] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const [searchQuery, setSearchQuery] = useState('')
  const workspaceSelectorRef = useRef(null)
  const menuPortalRef = useRef(null)
  const searchInputRef = useRef(null)
  const customFetch = useCustomFetch()
  const { isCollapsed } = useSidebarStore()

  useOutsideClick(workspaceSelectorRef, () => {
    if (workspaceSelectorRef) {
      setOpenMenu(false)
      setSearchQuery('')
    }
  }, [menuPortalRef])

  // Focus search input when menu opens
  React.useEffect(() => {
    if (openMenu && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }, [openMenu])

  async function handleChangeActiveWorkspace(newActiveWorkspace) {
    if (user.active_workspace === newActiveWorkspace) return
    try {
      await customFetch('/updateActiveWorkspace', 'PUT', { workspaceId: newActiveWorkspace })
      window.location.reload()
    } catch (err) {
      console.error(err)
    }
  }

  const getWorkspaceAcronym = (name) => {
    if (!name) return 'WS'
    const words = name.split(' ')
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  // Filter workspaces by search query
  const filteredWorkspaces = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return Object.keys(user.workspaces || {}).filter((workspace) => Boolean(workspaces[workspace]))
    }
    return Object.keys(user.workspaces || {})
      .filter((workspace) => Boolean(workspaces[workspace]))
      .filter((workspace) => workspaces[workspace].name.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [searchQuery, user.workspaces, workspaces])

  if (
    !workspaces ||
    !Object.keys(workspaces).length ||
    !user.active_workspace ||
    !Object.keys(user.active_workspace).length
  ) {
    return (
      <div
        className={`${isCollapsed ? 'mt-8 mx-auto' : 'mt-[41px] ml-[20px] w-[160px]'} ${isCollapsed ? 'w-[28px] h-[28px]' : 'p-2'} bg-white dark:bg-gray-800 rounded shadow border border-grey-200 dark:border-gray-700 relative`}
      >
        {isCollapsed ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#FF4A8E]"></div>
          </div>
        ) : (
          <button className="mx-2 flex items-center w-full justify-between">
            <p className="opacity-80 text-black dark:text-white text-[13px] font-bold font-['Manrope'] leading-none">
              Loading...
            </p>
            <div className="mr-2">
              <CircleChevronDown isOpen={openMenu} />
            </div>
          </button>
        )}
      </div>
    )
  }

  const activeWorkspaceName = workspaces[user.active_workspace].name
  const acronym = getWorkspaceAcronym(activeWorkspaceName)

  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={0} skipDelayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="mt-8 mx-auto w-[28px] h-[28px] bg-grey-100 dark:bg-gray-800 rounded-lg shadow-md border border-grey-200 dark:border-gray-700 relative cursor-pointer hover:opacity-90 transition-opacity"
              ref={workspaceSelectorRef}
              onClick={() => {
                const rect = workspaceSelectorRef.current?.getBoundingClientRect()
                if (rect) {
                  setMenuPosition({ top: rect.top, left: rect.right + 8 })
                }
                setOpenMenu(!openMenu)
              }}
            >
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-[#2A334E] dark:text-white text-[11px] font-bold font-['Manrope'] leading-none tracking-tight">
                  {acronym}
                </p>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-black text-white border-black">
            <p>{activeWorkspaceName}</p>
          </TooltipContent>
        </Tooltip>
        {openMenu &&
          createPortal(
            <WorkspaceMenuPortal
              menuPortalRef={menuPortalRef}
              menuPosition={menuPosition}
              searchInputRef={searchInputRef}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filteredWorkspaces={filteredWorkspaces}
              handleChangeActiveWorkspace={handleChangeActiveWorkspace}
              user={user}
              workspaces={workspaces}
            />,
            document.body
          )}
      </TooltipProvider>
    )
  }

  return (
    <div
      className="mt-[41px] ml-[20px] w-[160px] p-2 bg-grey-100 dark:bg-gray-800 rounded shadow border border-grey-200 dark:border-gray-700 relative"
      ref={workspaceSelectorRef}
    >
      <button
        className="mx-2 flex items-center w-full justify-between"
        onClick={() => {
          const rect = workspaceSelectorRef.current?.getBoundingClientRect()
          if (rect) {
            setMenuPosition({ top: rect.bottom + 8, left: rect.left })
          }
          setOpenMenu(!openMenu)
        }}
      >
        <p className="opacity-80 text-[#2A334E] dark:text-white text-[13px] font-bold font-['Manrope'] leading-none">
          {activeWorkspaceName}
        </p>
        <div className="mr-2">
          <CircleChevronDown isOpen={openMenu} />
        </div>
      </button>
      {openMenu &&
        createPortal(
          <WorkspaceMenuPortal
            menuPortalRef={menuPortalRef}
            menuPosition={menuPosition}
            searchInputRef={searchInputRef}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filteredWorkspaces={filteredWorkspaces}
            handleChangeActiveWorkspace={handleChangeActiveWorkspace}
            user={user}
            workspaces={workspaces}
          />,
          document.body
        )}
    </div>
  )
}
